import { useQuery } from "@tanstack/react-query";
import { CreditCard, Globe, Rocket } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { MethodIcon } from "@/components/billing/PayIcon";
import { ErrorState, FullPageSpinner, cx, Section, buttonClass } from "@/components/ui";
import { portal } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { methodDetail, methodProblem } from "@/lib/payments";
import { BackLink, PageTitle, SectionTitle, Confirmation } from "@/components/billing/parts";

/** Figma "Billing — Card saved (no charge made)". */
export function CardSavedPage() {
  const { methodId = "" } = useParams();
  const { orgId } = useOrganization();
  const methods = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => portal.paymentMethods(orgId),
  });
  const sub = useQuery({ queryKey: ["subscription", orgId], queryFn: () => portal.subscription(orgId) });

  if (methods.isPending || sub.isPending) return <FullPageSpinner />;
  if (methods.isError) return <ErrorState error={methods.error} onRetry={() => void methods.refetch()} />;
  if (sub.isError) return <ErrorState error={sub.error} onRetry={() => void sub.refetch()} />;

  const all = methods.data.payment_methods;
  const saved = all.find((m) => m.id === methodId);
  const { account } = sub.data;
  const plan = account.plan;
  const paid = plan.monthly_price_cents > 0;
  // A saved Apple Pay card or a bank account only pays renewals; buying something on the page uses a card.
  const renewalsOnly = saved?.renewals_only === true;
  const bank = saved?.kind === "bacs_debit";

  return (
    <div className="flex flex-col gap-6">
      <BackLink to="/billing?tab=payment-methods" />
      <PageTitle
        title={
          bank
            ? "Direct Debit set up"
            : saved?.wallet === "apple_pay"
              ? "Apple Pay saved"
              : saved?.wallet === "google_pay"
                ? "Google Pay saved"
                : "Card saved"
        }
        subtitle={`Nothing has been charged. You are still on the ${plan.name} plan.`}
      />
      {saved && (
        <Confirmation title={`${saved.label} is on file`}>
          {bank
            ? "Your bank account is set up for Direct Debit. You'll get an email confirming it, and notice before each debit. No payment was taken."
            : "Your bank confirmed the card is valid. No payment was taken."}
        </Confirmation>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <Section variant="compact">
            <SectionTitle>When this card will be used</SectionTitle>
            <div className="h-3" />
            {renewalsOnly ? (
              <Use icon={<Globe className="size-4" aria-hidden />} title="Domain renewals">
                Charged on each domain's renewal date. Buying a new domain uses a card.
              </Use>
            ) : (
              <Use icon={<Globe className="size-4" aria-hidden />} title="If you register a domain">
                Charged once at purchase, then on its own renewal date each year
              </Use>
            )}
            {paid ? (
              <Use icon={<Rocket className="size-4" aria-hidden />} title={`Your ${plan.name} plan`}>
                {saved?.is_default
                  ? `Renews on ${formatDate(account.current_period_end)} ${bank ? "by Direct Debit from this account" : "on this card"}, the default`
                  : "Renews on your default card; make this one the default in Billing to use it instead"}
              </Use>
            ) : bank ? (
              <Use icon={<Rocket className="size-4" aria-hidden />} title="If you move to a paid plan">
                Choose Bank debit at checkout: the plan starts once the debit clears, within 7 working days
              </Use>
            ) : renewalsOnly ? (
              <Use icon={<Rocket className="size-4" aria-hidden />} title="If you move to a paid plan">
                The upgrade is paid with a card, then renewals can be charged here when it's the default
              </Use>
            ) : (
              <Use icon={<Rocket className="size-4" aria-hidden />} title="If you move to a paid plan">
                Charged on the day you confirm the upgrade, then monthly
              </Use>
            )}
            <Use icon={<CreditCard className="size-4" aria-hidden />} title="Nothing else">
              {paid
                ? "There is no other recurring charge on this card"
                : "Free has no recurring charge, so this card sits unused until one of the above"}
            </Use>
          </Section>

          <Section variant="compact">
            <SectionTitle>Cards on file</SectionTitle>
            <div className="h-3" />
            <div className="flex flex-col gap-2">
              {all.map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-[10px] bg-[#faf6ef] px-3.5 py-3">
                  <MethodIcon method={m} className="text-fg-muted" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg">{m.label}</p>
                    <p className={cx("text-xs", methodProblem(m) ? "text-[#b8291f]" : "text-[#766e6a]")}>
                      {methodDetail(m)}
                    </p>
                  </div>
                  {m.is_default && (
                    <span className="ml-auto text-xs font-semibold text-[#766e6a]">Default</span>
                  )}
                </div>
              ))}
            </div>
          </Section>
        </div>

        <Section variant="compact" className="w-full shrink-0 lg:w-[380px]">
          <SectionTitle size="md">What now</SectionTitle>
          <p className="mt-2.5 text-xs text-fg-muted">
            {paid
              ? "Your card is ready for renewals and anything you buy."
              : "Carry on for free, or upgrade whenever the business needs a custom domain and email."}
          </p>
          <div className="h-3.5" />
          <Link to="/billing" className={buttonClass("secondary", "block")}>
            Back to Billing
          </Link>
          <div className="h-2.5" />
          <Link to="/billing/plans" className={buttonClass("brand", "block")}>
            <Rocket className="size-4" aria-hidden /> See plans
          </Link>
        </Section>
      </div>
    </div>
  );
}

function Use({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[#faf6ef] text-fg-muted">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-fg">{title}</p>
        <p className="mt-[3px] text-xs text-[#766e6a]">{children}</p>
      </div>
    </div>
  );
}
